import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Form from 'components/form'
import Button from 'components/Button'
import { useQuery, useQueryClient } from 'react-query'
import ApiClient from 'data/api/api_client'
import { formatShortCurrency } from 'data/helpers/currency'
import { useForm } from 'react-hook-form'
import useErrorHandler from 'data/hooks/useErrorHandler'
import { formatDateReadable, getDisabledDates } from 'data/helpers/dates'
import { config } from 'data/config'
import { useDispatch, useSelector } from 'react-redux'
import { setArrival, setDeparture, setAdults, setChildren } from 'store/booking-request'
import PropertyDatePicker from 'components/dates/PropertyDatePicker'
import { formatDefault } from 'data/helpers/dates'
import { asDate, asDateNullable } from '@ama-selections/ui/src/helpers/dates'
import BookingRequestModal from 'components/modals/home/property/BookingRequestModal'

const AvailabilityForm = ({ property, className = '' }) => {
  const dispatch = useDispatch()
  const handleError = useErrorHandler()
  const queryClient = useQueryClient()

  const [bookingRequestModalOpen, setBookingRequestModalOpen] = useState(false)
  const calendarInfo = queryClient.getQueryData('calendar')

  const { arrival, departure, adults, children } = useSelector(state => ({
    arrival: state.bookingRequest.arrival,
    departure: state.bookingRequest.departure,
    adults: state.bookingRequest.adults,
    children: state.bookingRequest.children,
  }))

  const { handleSubmit, setValue, formState: { errors }, setError, clearErrors } = useForm({
    defaultValues: {
      arrival: asDate(arrival),
      departure: asDate(departure),
      adults: adults,
      children: children,
    },
  })

  useEffect(() => {
    setValue('arrival', arrival)
    setValue('departure', departure)
    setValue('adults', adults)
    setValue('children', children)
  }, [arrival, departure, adults, children])

  const { data: availabilityResult, isLoading } = useQuery(
    [arrival, departure],
    () => {
      if (arrival || departure) {
        return ApiClient.properties.checkAvailability(property.id, {
          arrival: formatDefault(arrival),
          departure: formatDefault(departure),
        })
      }
    },
    {
      keepPreviousData: true,
      onSuccess: () => {
        clearErrors()
      },
      onError: (error) => {
        clearErrors()
        handleError(error, setError)
      },
      initialData: [],
    })

  const submitForm = () => {
    setBookingRequestModalOpen(true)
  }

  const onDayChange = (datesToFrom) => {
    dispatch(setArrival(formatDateReadable(datesToFrom.from ? datesToFrom.from : null)))
    dispatch(setDeparture(formatDateReadable(datesToFrom.to ? datesToFrom.to : null)))

    setValue('arrival', datesToFrom.from)
    setValue('departure', datesToFrom.to)
  }

  const adultCrement = (numAdults) => {
    dispatch(setAdults(numAdults))
    setValue('adults', numAdults)
  }

  const childCrement = (numChildren) => {
    dispatch(setChildren(numChildren))
    setValue('children', numChildren)
  }

  return (
    <div className="w-full max-w-[430px] mx-auto">
      <form className={`antialiased shadow-availability-form lg:shadow-none rounded-4 lg:rounded-none px-15 flex flex-col items-center gap-15 lg:gap-20 ${className}`} onSubmit={handleSubmit(submitForm)}>
        <PropertyDatePicker
          className="flex flex-col gap-20"
          errors={availabilityResult?.available
            ? errors
            : arrival && departure
              ? errors
              : null
          }
          formatDate={formatDateReadable}
          selected={{
            from: asDateNullable(arrival),
            to: asDateNullable(departure),
          }}
          disabledDays={getDisabledDates(calendarInfo?.data, property?.is_off_market)}
          onDayChange={onDayChange}
        />
        <div className="flex flex-col w-full lg:flex-row gap-15">
          <div className="flex w-full px-20 antialiased border text-grey-800 whitespace-nowrap text-16 border-home-divider h-60 rounded-4">
            <Form.Number
              placeholder="Adults"
              min={0}
              onChange={numAdults => adultCrement(numAdults)}
              value={adults ? adults + ' Adult' + (adults > 1 ? 's' : '') : null}
              error={availabilityResult?.available ? errors?.adults?.message : null}
            />
          </div>
          <div className="flex w-full px-20 antialiased border text-grey-800 whitespace-nowrap text-16 border-home-divider h-60 rounded-4">
            <Form.Number
              placeholder="Children"
              min={0}
              value={children ? children + ' Child' + (children > 1 ? 'ren' : '') : null}
              onChange={numAdults => childCrement(numAdults)}
              error={availabilityResult?.available ? errors?.children?.message : null}
            />
          </div>
        </div>

        <h3 className={`font-serif text-28 py-10 transition-all duration-300 ease-out block ${availabilityResult?.length === 0 ? 'opacity-0 h-0' : ''}`}>
          {arrival && departure && availabilityResult?.available
            ? availabilityResult?.price
              ? `Subtotal: ${formatShortCurrency(availabilityResult?.price, availabilityResult?.currency)}`
              : 'Price upon request'
            : ''
          }
        </h3>

        <div className="max-w-[260px] w-full">
          <Button
            type="submit"
            block
            isLoading={isLoading}
          >
            {arrival && departure
              ? 'INQUIRE NOW'
              : 'ADD DATES FOR PRICE'
            }
          </Button>
          <p className="mt-20 text-center text-grey-800 leading-20">
            Or call us at: <a id="call-ama-selections" href={`tel:${config.amaSelectionsPhoneNumber}`} className="underline">{config.amaSelectionsDisplayPhoneNumber}</a>
          </p>
        </div>

        <BookingRequestModal
          isOpen={bookingRequestModalOpen}
          onClose={() => setBookingRequestModalOpen(false)}
          property={property}
        />
      </form>
    </div>
  )
}

AvailabilityForm.propTypes = {
  property: PropTypes.object,
  className: PropTypes.string,
}

export default AvailabilityForm
